import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Percent, Calendar, Save, Building2, Receipt } from "lucide-react";
import { toast } from "sonner";

const SAMPLE_RENTAL_EUR = 100; // canonical sample used in the live split preview

/**
 * Per-vendor commission editor. Three mutually-exclusive modes:
 *   - default          → fall back to the platform-wide commission percent
 *   - percent          → vendor-specific percent override (0–50%)
 *   - monthly_per_car  → flat EUR/car/month fee, zero per-booking fee
 *
 * Admin-only (the PATCH endpoint enforces it). Renders as a controlled <Dialog> so the
 * parent (admin vendors table) just owns the open state and the target vendor.
 */
export default function VendorCommissionDialog({ vendor, open, onClose, onSaved }) {
  const [mode, setMode] = useState("default");
  const [value, setValue] = useState("");
  // Reduced per-booking % when on monthly_per_car. 0 = monthly fully replaces
  // commission; non-zero = hybrid (small per-booking on top of the monthly fee).
  const [reducedPercent, setReducedPercent] = useState("");
  const [saving, setSaving] = useState(false);
  // Platform-wide commission %, used to render the "Default" split preview honestly.
  const [platformPercent, setPlatformPercent] = useState(null);

  useEffect(() => {
    if (!open || !vendor) return;
    setMode(vendor.commission_mode || "default");
    setValue(vendor.commission_value != null ? String(vendor.commission_value) : "");
    setReducedPercent(vendor.commission_reduced_percent != null ? String(vendor.commission_reduced_percent) : "0");
    // Fire-and-forget: surface the global rate so the admin sees what
    // "Default" resolves to without leaving the dialog.
    api.get("/admin/settings")
      .then((r) => setPlatformPercent(Number(r.data?.commission_percent ?? 12)))
      .catch(() => setPlatformPercent(12));
  }, [open, vendor]);

  // Resolve the effective per-booking % implied by the current form state.
  // Mirrors backend `_resolve_vendor_fee_rate` so the preview never disagrees
  // with what the platform will actually charge.
  const effectivePercent = useMemo(() => {
    if (mode === "percent") {
      const v = Number(value);
      return Number.isFinite(v) ? Math.max(0, Math.min(v, 50)) : 0;
    }
    if (mode === "monthly_per_car") {
      const v = Number(reducedPercent);
      return Number.isFinite(v) ? Math.max(0, Math.min(v, 50)) : 0;
    }
    return platformPercent ?? 0;
  }, [mode, value, reducedPercent, platformPercent]);

  // Sample split for a canonical €100 rental — surfaces exactly how the
  // commission rate maps onto the renter-side payment split (paid online vs
  // at pickup). Deposit is rendered separately because it's per-vehicle and
  // doesn't enter the commission calculation.
  const split = useMemo(() => {
    const commission = +(SAMPLE_RENTAL_EUR * (effectivePercent / 100)).toFixed(2);
    const dueAtPickup = +(SAMPLE_RENTAL_EUR - commission).toFixed(2);
    const monthlyFee = mode === "monthly_per_car" ? Number(value || 0) : 0;
    return { commission, dueAtPickup, monthlyFee };
  }, [effectivePercent, mode, value]);

  const submit = async () => {
    if (mode !== "default") {
      const num = Number(value);
      if (Number.isNaN(num)) { toast.error("Value must be a number"); return; }
      if (num < 0) { toast.error("Value must be non-negative"); return; }
      if (mode === "percent" && num > 50) { toast.error("Percent must be ≤ 50"); return; }
    }
    let reducedNum = 0;
    if (mode === "monthly_per_car") {
      reducedNum = Number(reducedPercent || 0);
      if (Number.isNaN(reducedNum) || reducedNum < 0 || reducedNum > 50) {
        toast.error("Reduced commission must be 0–50%"); return;
      }
    }
    setSaving(true);
    try {
      const payload = mode === "default"
        ? { mode }
        : mode === "monthly_per_car"
          ? { mode, value: Number(value), reduced_percent: reducedNum }
          : { mode, value: Number(value) };
      await api.patch(`/admin/vendors/${vendor.id}/commission`, payload);
      toast.success(`Commission set to ${describe(mode, value, reducedNum)}`);
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!vendor) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Building2 className="h-5 w-5" />Commission for {vendor.business_name || vendor.name}</DialogTitle>
          <DialogDescription>Choose how this vendor pays the platform. Falls back to the global rate when "Default" is selected.</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-2">
          <ModeOption
            checked={mode === "default"}
            onClick={() => setMode("default")}
            title="Default · platform rate"
            description={
              platformPercent != null
                ? `Use the platform-wide rate (currently ${platformPercent}% per rental). Recommended for most vendors.`
                : "Use the platform-wide rate. Recommended for most vendors."
            }
            icon={Building2}
            testId="commission-mode-default"
          />
          <ModeOption
            checked={mode === "percent"}
            onClick={() => setMode("percent")}
            title="Percent override per rental"
            description="Custom % deducted from each rental total (excludes deposit). 0–50%."
            icon={Percent}
            testId="commission-mode-percent"
          >
            <div className="flex items-center gap-2 mt-2">
              <Input
                type="number"
                min={0}
                max={50}
                step={0.5}
                value={mode === "percent" ? value : ""}
                onChange={(e) => setValue(e.target.value)}
                onFocus={() => setMode("percent")}
                placeholder="e.g. 8"
                className="rounded-xl w-32"
                data-testid="commission-percent-value"
              />
              <span className="text-sm text-neutral-500">%</span>
            </div>
          </ModeOption>
          <ModeOption
            checked={mode === "monthly_per_car"}
            onClick={() => setMode("monthly_per_car")}
            title="Monthly flat fee per car"
            description="Charged regardless of bookings. By default zero per-booking fee — set a reduced % below for a hybrid plan."
            icon={Calendar}
            testId="commission-mode-monthly"
          >
            <div className="flex items-center gap-2 mt-2">
              <span className="text-sm text-neutral-500">€</span>
              <Input
                type="number"
                min={0}
                step={0.5}
                value={mode === "monthly_per_car" ? value : ""}
                onChange={(e) => setValue(e.target.value)}
                onFocus={() => setMode("monthly_per_car")}
                placeholder="e.g. 2"
                className="rounded-xl w-32"
                data-testid="commission-monthly-value"
              />
              <span className="text-sm text-neutral-500">/ car / month</span>
            </div>
            {mode === "monthly_per_car" && (
              <div className="mt-3 pt-3 border-t border-blue-100">
                <Label className="text-[10px] uppercase tracking-wider text-neutral-500">Reduced commission per booking (optional, 0–50%)</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    type="number"
                    min={0}
                    max={50}
                    step={0.5}
                    value={reducedPercent}
                    onChange={(e) => setReducedPercent(e.target.value)}
                    placeholder="0"
                    className="rounded-xl w-24"
                    data-testid="commission-monthly-reduced"
                  />
                  <span className="text-sm text-neutral-500">% on each rental, on top of the monthly fee</span>
                </div>
                <p className="text-[11px] text-neutral-500 mt-1">Set to 0 to make the monthly fee fully replace per-booking commission.</p>
              </div>
            )}
          </ModeOption>
        </div>

        {/* Iter 67bm — live split-payment preview. Shows the admin exactly how
            the chosen commission rate translates into the renter-side payment
            split for a canonical €100 rental: what the customer pays online
            (the platform's cut at checkout) vs what they owe the vendor at
            pickup. Mirrors `_resolve_vendor_fee_rate` + the quote endpoint. */}
        <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50/40 p-3.5" data-testid="commission-split-preview">
          <div className="flex items-center gap-2 mb-2">
            <Receipt className="h-4 w-4 text-blue-700" />
            <span className="text-[10px] uppercase tracking-wider font-bold text-blue-700">Sample €100 rental · split preview</span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <div className="rounded-lg bg-white px-2.5 py-2 ring-1 ring-blue-100">
              <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">Paid online to platform</div>
              <div className="font-bold text-blue-900 tabular-nums text-base mt-0.5" data-testid="commission-split-online">€{split.commission.toFixed(2)}</div>
              <div className="text-[10px] text-neutral-500">{effectivePercent.toFixed(2)}% of rental total</div>
            </div>
            <div className="rounded-lg bg-white px-2.5 py-2 ring-1 ring-blue-100">
              <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-semibold">Due to vendor at pickup</div>
              <div className="font-bold text-emerald-700 tabular-nums text-base mt-0.5" data-testid="commission-split-pickup">€{split.dueAtPickup.toFixed(2)}</div>
              <div className="text-[10px] text-neutral-500">+ vehicle deposit, refundable</div>
            </div>
          </div>
          {mode === "monthly_per_car" && split.monthlyFee > 0 && (
            <div className="mt-2 rounded-lg bg-amber-50 ring-1 ring-amber-200 px-2.5 py-1.5 text-[11px] text-amber-900" data-testid="commission-split-monthly-note">
              <strong>Plus:</strong> €{split.monthlyFee.toFixed(2)} / car / month invoiced separately to the vendor.
            </div>
          )}
          <p className="text-[10px] text-neutral-500 mt-2">Deposit is per-vehicle and excluded from the commission calculation. Route fees, insurance, add-ons and Meet & Greet are included in the rental total before the % is applied.</p>
        </div>

        <div className="flex justify-end gap-2 mt-4">
          <Button variant="ghost" onClick={onClose} className="rounded-full" data-testid="commission-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="rounded-full bg-blue-600 hover:bg-blue-700" data-testid="commission-save">
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Save className="h-4 w-4 mr-1.5" />}Save
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ModeOption({ checked, onClick, title, description, icon: Icon, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`w-full text-left rounded-xl border p-3.5 transition flex gap-3 items-start ${
        checked ? "border-blue-500 bg-blue-50/40 ring-2 ring-blue-100" : "border-border hover:border-neutral-300 hover:bg-neutral-50"
      }`}
    >
      <Label className={`h-5 w-5 mt-0.5 rounded-full border flex items-center justify-center shrink-0 ${checked ? "border-blue-500" : "border-neutral-300"}`}>
        {checked && <span className="h-2.5 w-2.5 rounded-full bg-blue-600" />}
      </Label>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 text-sm font-medium"><Icon className="h-3.5 w-3.5 text-neutral-500" />{title}</div>
        <div className="text-xs text-neutral-500 mt-0.5">{description}</div>
        {children}
      </div>
    </button>
  );
}

export function commissionLabel(vendor) {
  const mode = vendor?.commission_mode || "default";
  const value = vendor?.commission_value;
  const reduced = Number(vendor?.commission_reduced_percent || 0);
  if (mode === "percent" && value != null) return `${value}%`;
  if (mode === "monthly_per_car" && value != null) {
    return reduced > 0 ? `€${value}/car/mo + ${reduced}%` : `€${value}/car/mo`;
  }
  return "Default";
}

function describe(mode, value, reducedNum = 0) {
  if (mode === "default") return "default platform rate";
  if (mode === "percent") return `${value}% per rental`;
  if (mode === "monthly_per_car") {
    return reducedNum > 0
      ? `€${value} / car / month + ${reducedNum}% per rental`
      : `€${value} / car / month`;
  }
  return mode;
}
